import React, { useState } from "react";
import { createFragmentContainer } from "react-relay";
import { Match } from "found";

import { IViewer } from "@schema";
import { withRouteQuery } from "@routing/routes.util";
import { getCurrentOrg } from "@util/org.util";

import { adminPanelContainerFragments, adminPanelContainerQuery } from "./AdminPanel.gql";
import { AdminPanel } from "./AdminPanel";
import { getOrgOverview } from "./AdminPanel.util";

import { paths } from "@clockwise/client-commons/src/constants/site";

export enum AdminPanelViews {
  Overview = "Overview",
  Members = "Members",
  PlansAndBilling = "PlansAndBilling",
  Settings = "Settings",
}

enum AdminPanelRoutes {
  Overview = "overview",
  Members = "members",
  PlansAndBilling = "billing",
  Settings = "settings",
}

interface IAdminPanelContainerProps {
  viewer: IViewer;
  match: Match;
}

const AdminPanelContainerCmpt = ({ viewer, match }: IAdminPanelContainerProps) => {
  const org = getCurrentOrg(viewer);

  if (!org) {
    return null;
  }

  const getFirstEnabledAdminPanelView = () => {
    const orgOverview = getOrgOverview(org);

    const defaultView = orgOverview ? AdminPanelViews.Overview : AdminPanelViews.Members;
    const selectedView = match.params["view"] || "";

    switch (selectedView) {
      case AdminPanelRoutes.Overview:
        return AdminPanelViews.Overview;
      case AdminPanelRoutes.Members:
        return AdminPanelViews.Members;
      case AdminPanelRoutes.PlansAndBilling:
        return AdminPanelViews.PlansAndBilling;
      case AdminPanelRoutes.Settings:
        return AdminPanelViews.Settings;
      default:
        return defaultView;
    }
  };

  const [currentView, setCurrentView] = useState(getFirstEnabledAdminPanelView());

  if (!currentView) {
    return null;
  }

  const getPathForView = (view: AdminPanelViews) => {
    switch (view) {
      case AdminPanelViews.Overview:
        return AdminPanelRoutes.Overview;
      case AdminPanelViews.Members:
        return AdminPanelRoutes.Members;
      case AdminPanelViews.PlansAndBilling:
        return AdminPanelRoutes.PlansAndBilling;
      case AdminPanelViews.Settings:
        return AdminPanelRoutes.Settings;
      default:
        return "";
    }
  };

  const updateCurrentView = (selectedView: AdminPanelViews) => {
    const path = getPathForView(selectedView);
    window.history.pushState("", "", `${paths.admin}${path ? `/${path}` : ``}`); // set url

    setCurrentView(selectedView);
  };

  return (
    <AdminPanel
      match={match}
      org={org}
      currentView={currentView}
      updateCurrentView={updateCurrentView}
    />
  );
};

export const AdminPanelContainer = createFragmentContainer<IAdminPanelContainerProps>(
  AdminPanelContainerCmpt,
  adminPanelContainerFragments,
);

export const AdminPanelPage = withRouteQuery(AdminPanelContainer, adminPanelContainerQuery);
